/**
 * Admin card to download and upload open RMAs JSON for a selected Global-e portal (activated merchant).
 */
import { observer } from "mobx-react-lite";
import React from "react";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { Button } from "../../../components/inputs/button/button";
import Card from "../../../components/layouts/card/card";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import ShopsSelector from "../../../components/shops-selector/shops-selector";
import { FormField } from "../../../components/form-field/form-field";
import { FormFieldLine } from "../../../components/form-field-line/form-field-line";
import FileUpload from "../../../components/file-upload/file-upload";
import rmasJsonDownloadUploadStore from "./stores/rmas-json-download-upload-store";
import supportedGlobalEEnvsStore from "./stores/supported-global-e-envs-store";
import { isSet } from "../../../models/model-types-methods";
import "./migrate-global-e-merchants.scss";
import { IShop } from "../../../models/User";

/** Includes empty string for browsers that omit `file.type` for some `.json` picks. */
const JSON_MIME_TYPES = ["application/json", "text/plain", ""];

const RMAsJsonDownloadUploadCard: React.FC = observer(() => {
    const store = rmasJsonDownloadUploadStore;
    const areEnvironmentsLoading = supportedGlobalEEnvsStore.isLoading || !supportedGlobalEEnvsStore.isLoaded;

    return (
        <Card
            isExpanded={true}
            toolbar={{
                title: (
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        RMAs JSON Download & Upload
                    </FontBody1>
                ),
            }}
        >
            <ListLayout>
                <FormFieldLine label="Select a Global-e portal">
                    <ShopsSelector
                        value={store.portal ? [store.portal] : ([] as IShop[])}
                        onChange={(shops: IShop[]) => store.setPortal(shops[0])}
                        disabled={store.isProcessing}
                        includeSecondaryPortals
                    />
                </FormFieldLine>

                <FormField label="Download Open RMAs JSON file">
                    <Button
                        onClick={() => store.download()}
                        text="Download"
                        hasLoader={true}
                        isLoading={store.isProcessing}
                        disabled={areEnvironmentsLoading || store.isProcessing || !isSet(store.portal)}
                    />
                </FormField>

                <FormFieldLine label="JSON file">
                    <FileUpload
                        uploadFile={(file) => {
                            store.setJsonFile(file);
                        }}
                        buttonText="Select JSON file"
                        acceptedFileFormats=".json,application/json"
                        fileTypes={JSON_MIME_TYPES}
                        disabled={store.isProcessing}
                        isLoading={store.isProcessing}
                    />
                    {store.file ? (
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>{store.file.name}</FontBody1>
                    ) : null}
                </FormFieldLine>

                <Button
                    onClick={() => store.upload()}
                    text="Upload"
                    hasLoader={true}
                    isLoading={store.isProcessing}
                    disabled={
                        areEnvironmentsLoading
                        || store.isProcessing
                        || !isSet(store.portal)
                        || !store.file
                    }
                />

                {store.infoMessage ? (
                    <div className="results-message">
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>{store.infoMessage}</FontBody1>
                    </div>
                ) : null}
            </ListLayout>
        </Card>
    );
});

export default RMAsJsonDownloadUploadCard;
